// 戦力を上げる提案（負けたときの戦果画面など）：今の手持ちでできることを試算し、戦力の上がり幅の大きい順に並べる。
// 試算は端末だけ（profile を写して powerOf を測る）。実際の操作は各画面で行う（ここからはその画面へ案内するだけ）。
//   付け替え：持っている装備（他のキャラが付けていないもの）で各部位をいちばん強いものに
//   お守り：付けていないお守りの中でいちばん戦力が上がるもの
//   装備の強化：手持ちの銭で、付けている装備を「銭あたりの上がり幅」が大きい順に強化
//   式神の強化：手持ちの霊玉で、連れていく式神の式神Lv を上げる
import { CHAR_IDS, enhanceCost, levelCap, omamoriOwner, shikiLevel, shikiLevelCap, shikiLevelUpCost, type CharId, type EquipItem, type EquipSlot, type Profile } from '@hyakki/core';
import { powerOf } from '../core-bridge';

export interface PowerHint {
  kind: 'swap' | 'omamori' | 'enhance' | 'shiki';
  title: string;
  sub: string;
  after: number;
  /** 案内する画面 */
  to: { screen: string; params: Record<string, string | number> };
}

const SLOTS: EquipSlot[] = ['weapon', 'armor', 'netsuke'];
const clone = (p: Profile): Profile => structuredClone(p);
const pw = (p: Profile, c: CharId) => powerOf(p, c) ?? 0;
const ownerOf = (p: Profile, id: number) => CHAR_IDS.find((c) => Object.values(p.sets[c] ?? {}).includes(id)) ?? null;

/** 付け替え：部位ごとに、いちばん戦力が上がる装備に（順に決める） */
function swapPlan(p: Profile, c: CharId): { p: Profile; n: number } {
  const q = clone(p);
  let n = 0;
  for (const slot of SLOTS) {
    const cands = q.items.filter((it) => it.slot === slot && (!it.charId || it.charId === c) && (ownerOf(q, it.id) ?? c) === c);
    let best = { id: q.sets[c]?.[slot] ?? null, power: pw(q, c) };
    for (const it of cands) {
      if (it.id === best.id) continue;
      const t = clone(q);
      t.sets[c] = { ...t.sets[c], [slot]: it.id };
      const v = pw(t, c);
      if (v > best.power + 0.5) best = { id: it.id, power: v };
    }
    if (best.id !== (q.sets[c]?.[slot] ?? null)) {
      q.sets[c] = { ...q.sets[c], [slot]: best.id };
      n++;
    }
  }
  return { p: q, n };
}

/** お守り：付けていないもの（他のキャラのものは除く）から、いちばん戦力が上がる1つ */
function omamoriPlan(p: Profile, c: CharId): { p: Profile; name: string | null } {
  const cur = p.omamoriSets?.[c]?.[0] ?? null;
  let best = { id: cur, power: pw(p, c) };
  for (const o of p.omamori ?? []) {
    const by = omamoriOwner(p, o.id);
    if (o.id === cur || (by && by !== c)) continue;
    const t = clone(p);
    t.omamoriSets = { ...t.omamoriSets, [c]: [o.id] };
    const v = pw(t, c);
    if (v > best.power + 0.5) best = { id: o.id, power: v };
  }
  if (best.id === cur) return { p, name: null };
  const q = clone(p);
  q.omamoriSets = { ...q.omamoriSets, [c]: [best.id] };
  return { p: q, name: String(best.id) };
}

/** 装備の強化：銭 zeni の範囲で、銭あたりの上がり幅が大きい +1 を順に */
function enhancePlan(p: Profile, c: CharId, zeni: number): { p: Profile; spent: number; ups: number } {
  const q = clone(p);
  let spent = 0;
  let ups = 0;
  for (let guard = 0; guard < 80; guard++) {
    const base = pw(q, c);
    let best: { it: EquipItem; cost: number; rate: number } | null = null;
    for (const slot of SLOTS) {
      const id = q.sets[c]?.[slot];
      const it = id != null ? q.items.find((x) => x.id === id) : undefined;
      if (!it || it.level >= levelCap(it)) continue;
      const cost = enhanceCost(it);
      if (spent + cost > zeni) continue;
      it.level++;
      const gain = pw(q, c) - base;
      it.level--;
      const rate = gain / Math.max(1, cost);
      if (gain > 0 && (!best || rate > best.rate)) best = { it, cost, rate };
    }
    if (!best) break;
    best.it.level++;
    spent += best.cost;
    ups++;
  }
  return { p: q, spent, ups };
}

/** 式神の強化：持っている式神（★の高い順、同じなら式神Lv の低い順）の式神Lv を霊玉の範囲で */
function shikiPlan(p: Profile, c: CharId, reidama: number): { p: Profile; id: string | null; from: number; to: number } {
  void c;
  const id =
    Object.keys(p.shikigami ?? {})
      .filter((x) => (p.shikigami?.[x]?.star ?? 0) > 0 && shikiLevel(p.shikigami[x]) < shikiLevelCap(p.shikigami[x].star))
      .sort((a, b) => p.shikigami[b].star - p.shikigami[a].star || shikiLevel(p.shikigami[a]) - shikiLevel(p.shikigami[b]))[0] ?? null;
  const st = id ? p.shikigami?.[id] : undefined;
  if (!id || !st || st.star <= 0) return { p, id: null, from: 0, to: 0 };
  const from = shikiLevel(st);
  const cap = shikiLevelCap(st.star);
  let to = from;
  let cost = 0;
  while (to < cap && cost + shikiLevelUpCost(to) <= reidama) cost += shikiLevelUpCost(to++);
  if (to === from) return { p, id: null, from, to };
  const q = clone(p);
  q.shikigami[id] = { ...q.shikigami[id], level: to };
  return { p: q, id, from, to };
}

/** 提案（上がり幅の大きい順）と、全部行ったときの戦力 */
export function powerHints(p: Profile, c: CharId, res: { zeni: number; reidama: number }): { hints: PowerHint[]; all: number; now: number } {
  const now = pw(p, c);
  const hints: PowerHint[] = [];
  const sw = swapPlan(p, c);
  if (sw.n) hints.push({ kind: 'swap', title: '装備を付け替える', sub: `${sw.n}か所`, after: pw(sw.p, c), to: { screen: 'chara', params: { tab: 'equip', c } } });
  const om = omamoriPlan(p, c);
  if (om.name) hints.push({ kind: 'omamori', title: 'お守りを付け替える', sub: '', after: pw(om.p, c), to: { screen: 'chara', params: { tab: 'equip', c } } });
  const en = enhancePlan(p, c, res.zeni);
  if (en.ups) hints.push({ kind: 'enhance', title: '装備を強化する', sub: `銭${en.spent.toLocaleString('ja-JP')}で +${en.ups}`, after: pw(en.p, c), to: { screen: 'enhance', params: { tab: 'equip' } } });
  const sk = shikiPlan(p, c, res.reidama);
  if (sk.id) hints.push({ kind: 'shiki', title: '式神を強化する', sub: `式神Lv${sk.from}→${sk.to}`, after: pw(sk.p, c), to: { screen: 'enhance', params: { tab: 'shiki', shiki: sk.id } } });
  // 全部：付け替え → お守り → 強化（付け替えた装備で）→ 式神
  let q = sw.p;
  q = omamoriPlan(q, c).p;
  q = enhancePlan(q, c, res.zeni).p;
  q = shikiPlan(q, c, res.reidama).p;
  return { hints: hints.filter((h) => h.after > now + 0.5).sort((a, b) => b.after - a.after), all: pw(q, c), now };
}
